import { useMemo, useState } from "react";
import { Building2 } from "lucide-react";
import { ToolLayout, TwoCol } from "@/components/tools/ToolLayout";
import { SectionCard, Field, MoneyInput, SliderRow, StatCard, InfoRow, compactINR } from "@/components/tools/kit";
import { rentalYield } from "@/lib/calculators";
import { CITY_BENCHMARKS } from "@/lib/tools";

export default function RentalYield() {
  const [propertyValue, setPV] = useState(12000000);
  const [monthlyRent, setRent] = useState(30000);
  const [annualExpenses, setExp] = useState(60000);
  const [appreciation, setApp] = useState(5);
  const r = useMemo(() => rentalYield({ propertyValue, monthlyRent, annualExpenses, annualAppreciation: appreciation }), [propertyValue, monthlyRent, annualExpenses, appreciation]);
  const rating = r.gross >= 4 ? { t: "Strong", c: "#2D6A4F" } : r.gross >= 3 ? { t: "Average", c: "#D99B26" } : { t: "Low", c: "#D96B43" };
  return (
    <ToolLayout title="Rental Yield Calculator" subtitle="Gross & net yield, benchmarked to Indian cities" icon={Building2} accent="#2A6F7F">
      <TwoCol
        left={
          <SectionCard title="Property details">
            <div className="space-y-5">
              <Field label="Property value"><MoneyInput testid="ry-value" value={propertyValue} onChange={setPV} helper={compactINR(propertyValue)} /></Field>
              <Field label="Monthly rent"><MoneyInput testid="ry-rent" value={monthlyRent} onChange={setRent} helper={compactINR(monthlyRent) + " / month"} /></Field>
              <Field label="Annual expenses (maintenance, tax)"><MoneyInput testid="ry-expenses" value={annualExpenses} onChange={setExp} helper={compactINR(annualExpenses) + " / year"} /></Field>
              <Field label="Expected appreciation (p.a.)" hint={appreciation + "%"}><SliderRow testid="ry-appreciation" value={appreciation} min={0} max={15} step={0.5} suffix="%" onChange={setApp} /></Field>
            </div>
          </SectionCard>
        }
        right={
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard testid="ry-gross" label="Gross Yield" value={r.gross.toFixed(2) + "%"} accent="#2A6F7F" big />
              <StatCard testid="ry-net" label="Net Yield" value={r.net.toFixed(2) + "%"} accent="#D99B26" />
              <StatCard testid="ry-total" label="Total Return*" value={r.totalReturn.toFixed(2) + "%"} accent="#2D6A4F" sub="incl. appreciation" />
            </div>
            <SectionCard title="Assessment">
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 rounded-full text-white text-sm font-bold" style={{ background: rating.c }} data-testid="ry-rating">{rating.t} yield</span>
                <span className="text-sm text-[#5C6A64]">Annual rent: {compactINR(r.annualRent)}</span>
              </div>
              <p className="text-xs text-[#8A9790] mt-3">Residential rental yields in India typically range 2.5%–4.5% gross. Net return adds capital appreciation.</p>
            </SectionCard>
            <SectionCard title="City benchmarks (typical gross yield)">
              <div className="space-y-1">
                {CITY_BENCHMARKS.map((c) => (
                  <InfoRow key={c.city} label={`${c.city} · ${c.tier}`} value={`${c.yield.toFixed(1)}%`} strong={Math.abs(c.yield - r.gross) < 0.4} />
                ))}
              </div>
            </SectionCard>
          </>
        }
      />
    </ToolLayout>
  );
}
